/**
 * Shared pieces of the "assign to" user picker.
 *
 * These live apart from `components/UserSelect.tsx` so that file exports a
 * component and nothing else: a module mixing components with plain exports
 * cannot be hot-reloaded reliably by React Fast Refresh (react-refresh/
 * only-export-components), so an edit there drops the page's state.
 *
 * Tasks, clients and leads all use these, which keeps one copy of the label rule
 * (full name, falling back to email) so the pages can't drift apart.
 */
import { useEffect, useState } from 'react'

import { listUsers } from '../services/users'

export interface UserOption {
  id: number
  email: string
  full_name: string | null
}

/** How an assignee is named everywhere in the CRM. */
export function userLabel(u: UserOption | null | undefined): string {
  return u?.full_name || u?.email || '—'
}

/**
 * Load the assignable users once.
 *
 * Failures are swallowed deliberately: an unreachable user list must not break
 * the page it sits on — the picker just shows "Unassigned".
 */
export function useUserOptions(): UserOption[] {
  const [users, setUsers] = useState<UserOption[]>([])
  useEffect(() => {
    listUsers()
      .then((res) => setUsers(res.users))
      .catch(() => {})
  }, [])
  return users
}
